import React, { useState, useEffect } from 'react';
import { learningHooksApi } from '../../services/api';
import { LearningHookCreator } from '../learning_hook/LearningHookCreator';

interface LearningHook {
  id: number;
  title: string;
  description: string;
  grade_levels: string;
  project_type: string;
  max_students: number;
  enrolled_count: number;
  is_active: boolean;
  created_at: string;
  teacher_name: string;
}

interface HookDashboardProps {
  isTeacher?: boolean;
}

export const HookDashboard: React.FC<HookDashboardProps> = ({ isTeacher = false }) => {
  const [hooks, setHooks] = useState<LearningHook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreator, setShowCreator] = useState(false);
  const [editingHook, setEditingHook] = useState<LearningHook | null>(null);

  useEffect(() => {
    fetchHooks();
  }, []);

  const fetchHooks = async () => {
    try {
      const response = await learningHooksApi.getAll();
      // Handle the response structure: {"learning_hooks": [...]}
      const hooksData = response.data.learning_hooks || response.data.results || response.data || [];
      setHooks(Array.isArray(hooksData) ? hooksData : []);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to fetch learning hooks');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (hook: LearningHook) => {
    setEditingHook(hook);
  };

  const handleDelete = async (hook: LearningHook) => {
    if (window.confirm(`Are you sure you want to delete "${hook.title}"?`)) {
      try {
        await learningHooksApi.delete(hook.id);
        fetchHooks();
      } catch (error: any) {
        setError('Failed to delete learning hook');
      }
    }
  };

  const handleHookCreated = () => {
    setShowCreator(false);
    setEditingHook(null);
    fetchHooks();
  };

  const getProjectTypeDisplay = (type: string) => {
    const types: { [key: string]: string } = {
      'individual': 'Individual',
      'group': 'Group',
      'class': 'Class Activity'
    };
    return types[type] || type;
  };

  const getCapacityDisplay = (hook: LearningHook) => {
    if (hook.max_students) {
      return `${hook.enrolled_count}/${hook.max_students}`;
    }
    return `${hook.enrolled_count} enrolled`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {isTeacher ? 'My Learning Hooks' : 'Learning Hooks'}
        </h2>
        {isTeacher && (
          <button
            onClick={() => setShowCreator(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          >
            Create Learning Hook
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {hooks.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🎯</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isTeacher ? 'No Learning Hooks Yet' : 'No Learning Hooks Available'}
          </h3>
          <p className="text-gray-500 mb-6">
            {isTeacher 
              ? 'Create your first learning hook to get started with project-based learning.'
              : 'Check back later for new learning hooks from your teachers.'
            }
          </p>
          {isTeacher && (
            <button
              onClick={() => setShowCreator(true)}
              className="bg-blue-500 text-white px-6 py-3 rounded-md hover:bg-blue-600"
            >
              Create Your First Learning Hook
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-6">
          {hooks.map((hook) => (
            <div key={hook.id} className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{hook.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{hook.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {hook.grade_levels}
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                      {getProjectTypeDisplay(hook.project_type)}
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                      {getCapacityDisplay(hook)}
                    </span>
                    {hook.is_active ? (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
                
                {isTeacher && (
                  <div className="flex space-x-2 ml-4">
                    <button 
                      onClick={() => handleEdit(hook)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(hook)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>{new Date(hook.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Learning Hook Creator Modal */}
      {(showCreator || editingHook) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <LearningHookCreator 
              onSuccess={handleHookCreated} 
              onCancel={() => {
                setShowCreator(false);
                setEditingHook(null);
              }}
              editingHook={editingHook}
            />
          </div>
        </div>
      )}
    </div>
  );
};
